import { ArrowBack, ArrowForward } from "@mui/icons-material";
import {
  Dialog,
  Typography,
  Container,
  Stack,
  Button,
  styled,
  LinearProgress,
  Box,
  Chip,
  Badge,
} from "@mui/material";
import useContact from "components/Appbar/Contact";
import { LogoFull } from "components/logo";
import ReactGA from "components/ReactGA";
import { APP_VERSION } from "config";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useFlag from "state/useFlag";
import { listFeature } from "./config/full";

const Image = styled("img")`
  ${({ theme }) => theme.breakpoints.only("xs")} {
    width: 100%;
  }
  ${({ theme }) => theme.breakpoints.up("sm")} {
    width: 507px;
  }
  ${({ theme }) => theme.breakpoints.up("lg")} {
    width: 676px;
  }
`;

const ContainerCentered = styled(Container)`
  margin-top: 48px;
`;

type WhatIsNewFullScreenProps = {
  open: boolean;
  handleClose: () => any;
};

const WhatIsNewFullScreen = ({ open, handleClose }: WhatIsNewFullScreenProps) => {
  const { t } = useTranslation(["translation", "newFeature"]);
  const [isActive, setIsActive] = useState(0);
  const { flags } = useFlag();
  const { handleContact } = useContact();
  const list = useMemo(() => listFeature(t, handleContact, handleClose), [t]);
  const contentActive = useMemo(() => list[isActive], [isActive]);
  const hiddenBackward = useMemo(() => isActive === 0, [isActive]);
  const lastShow = useMemo(() => list.length - 1 === isActive, [isActive]);
  const hiddenClose = useMemo(() => flags?.new_feature_full?.isFirst, [flags]);
  const progress = useMemo(() => ((isActive + 1) * 100) / list.length, [isActive]);

  const [read, setRead] = useState([0]);

  const handleBackward = () => {
    setIsActive(isActive - 1);
  };

  const handleForward = () => {
    setRead([...read, isActive + 1]);
    setIsActive(isActive + 1);
  };

  const handleGetStart = () => {
    ReactGA.event("what_is_new", {
      user_interaction: "get start",
    });
    handleClose();
  };

  return (
    <Dialog fullScreen open={open} sx={{ zIndex: 1500 }}>
      <LinearProgress
        variant="determinate"
        value={progress}
        color="secondary"
        sx={{ height: 10 }}
      />
      <Box sx={{ backgroundColor: "primary.main", py: 3 }}>
        <Container maxWidth={"xl"}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "center", sm: "flex-start" }}
            spacing={1.5}
          >
            <Stack spacing={1} alignItems={{ xs: "center", sm: "flex-start" }}>
              <LogoFull />
              <Typography sx={{ typography: { xs: "h5", sm: "h4" } }} color="white">
                {t("latest update")} M0 {APP_VERSION}
              </Typography>
            </Stack>
            <Button
              variant="rest"
              onClick={() => {
                ReactGA.event("what_is_new", {
                  user_interaction: "close",
                });
                handleClose();
              }}
              size="medium"
              sx={{ minWidth: 144, ...(hiddenClose && { visibility: "collapse" }) }}
            >
              {t("close this window")}
            </Button>
          </Stack>
        </Container>
      </Box>
      <Box>
        <ContainerCentered maxWidth={"xl"}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "center", md: "flex-start" }}
            spacing={3}
          >
            <Image src={contentActive.image} alt="" />
            <Stack>
              <Typography variant="h5" color="primary.main">
                {contentActive.title}
              </Typography>
              <Typography
                variant="body2"
                color={"text.secondary"}
                mt={2}
                sx={{ whiteSpace: "pre-line" }}
              >
                {contentActive.content}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            spacing={3}
            direction={{ xs: "column-reverse", md: "column" }}
            mt={{ xs: 3, lg: 6 }}
          >
            <Box
              sx={{
                width: "100%",
                background: "#F5F5F5",
                borderRadius: "8px",
              }}
            >
              <Stack
                direction={"row"}
                gap={1}
                flexWrap={"wrap"}
                sx={{
                  p: 1.5,
                }}
              >
                {list.map(({ title }, index) => (
                  <Box key={index}>
                    {/* <Badge color="warning" variant="dot"> */}
                    <Chip
                      label={title}
                      onClick={() => setIsActive(index)}
                      color={isActive === index ? "primary" : "neutral"}
                      variant={isActive === index ? "filled" : "outlined"}
                      sx={{
                        "& .MuiChip-label": { whiteSpace: "normal" },
                        height: "100%",
                        borderRadius: "4px",
                      }}
                      disabled={hiddenClose && !read.includes(index)}
                    />
                    {/* </Badge> */}
                  </Box>
                ))}
              </Stack>
            </Box>
            <Stack direction={"row"} justifyContent="space-between" alignItems="center">
              <Button
                color="neutral"
                startIcon={<ArrowBack />}
                onClick={handleBackward}
                sx={{ ...(hiddenBackward && { visibility: "collapse" }) }}
              >
                {t("backward")}
              </Button>
              {!lastShow ? (
                <Button
                  color="primary"
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={handleForward}
                  sx={{ minWidth: 144 }}
                >
                  {t("next")}
                </Button>
              ) : (
                <Button
                  color="primary"
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={handleGetStart}
                  sx={{ minWidth: 144 }}
                >
                  {t("get_started")}
                </Button>
              )}
            </Stack>
          </Stack>
        </ContainerCentered>
      </Box>
    </Dialog>
  );
};

export default WhatIsNewFullScreen;
